import * as React from "react";
import { isEmpty as _isEmpty, map as _map } from "lodash"
import { IPatientExamination, IPatientSystemiscExamination } from "@curefit/ehr-common";
import { IPatientDetail, IAppointmentScheduleResponse, ISymptom, IDiagnosis } from "@curefit/care-common";
import AppUtils from "../../utils/AppUtils";
import { AppConstants, vitalHeaderMap, PrescriptionPdfSection } from "../../utils/AppConstants";
import {
    getDivTemplate, getPdfColTemplate, getPdfRowTemplate,
    getSpanTemplate
} from "../../PdfTemplates/prescriptionBody";
import { IPdfTemplates } from "../../models/PdfTemplatesModels";
import { getLiTemplate } from "../../PdfTemplates/prescriptionBody/generalTemplate";
import ITreatmentPlanTemplateResponseObj, { TreatmentPlanMedicinesObj, TreatmentPlanLabTestObj, TreatmentPlanAdviceObj, TreatmentPlanReferralObj, TreatmentPlanFollowUpObj } from "../../models/ITreatmentPlanTemplateResponse";
import IDoctorSpeciality from "../../models/IDoctorSpeciality";
import IDoctorProfile from "../../models/IDoctorProfile";
import { IOngoingConsultation } from "../../models/IOngoingConsultation";
import { IPatientVitalResponse, IPatientVitalsUI } from "../../models/IPatientVitalResponse";
import IStaticDataValue from "../../../../common/models/IStaticDataValue";
import { DocSignatureMap } from "../../containers/Ongoing/ReviewPdf";
import { IEHRSavePatientMedicalProfile } from "../../models/patients/IEHRSavePatientMedicalProfile";
import { treatmentPlanModifyTemplate, treatmentPlanFormatState } from "../../utils/PdfUtils/TreatmentPlanPdfUtil";
import { doctorPrescriptionFormatState, doctorPrescriptionModifyTemplate } from "../../utils/PdfUtils/DoctorConsultationPdfUtil";
import { physioPrescriptionFormatState, physioPrescriptionModifyTemplate } from "../../utils/PdfUtils/PhysioConsultationPdfUtil";

interface CommonPrescriptionPdfProps {
    pdfTemplates: IPdfTemplates,
    isPhysioConsultation: boolean,
    isTreatmentPlan: boolean,
    consultationData?: IConsultationPdfData
    treatmentPlanData?: ITreatmentPlanPdfData
    hideHeaderFooter: boolean;
    renderHandler: () => void,
}

export interface IConsultationPdfData {
    vitals: IPatientVitalResponse[]
    examinations: IPatientExamination
    systemicExaminations: IPatientSystemiscExamination[]
    entExaminations: IPatientSystemiscExamination[]
    allergies: any
    appointmentSchedule: IAppointmentScheduleResponse
    patient: IPatientDetail
    signatureImagePreSignedUrl: DocSignatureMap
    staticSystemicExams: IStaticDataValue[]
    staticEntExams: IStaticDataValue[]
    staticSpecialistData: IDoctorSpeciality[]
    medicalProfile: IEHRSavePatientMedicalProfile
    consultation: IOngoingConsultation
}

export interface ITreatmentPlanPdfData {
    templateData: ITreatmentPlanTemplateResponseObj
    doctorProfile: IDoctorProfile
    staticSpecialistData: IDoctorSpeciality[]
}

const CommonPrescriptionPdf: React.FC<CommonPrescriptionPdfProps> = (props) => {

    const hasBeenCalled = React.useRef(false);
    const [pdfGenerated, setPdfGenerated] = React.useState<boolean>(false)
    const [prescriptionList, setPrescriptionList] = React.useState<Array<any>>([])
    const [template, setTemplate] = React.useState<Array<string>>([])

    React.useEffect(() => {
        // On Mount
        const prescriptionContainer = document.getElementsByClassName("prescription-card-content-container")[0];
        let prescription: any = getFormatPrescription();
        const maxHeight = props.isPhysioConsultation ? AppConstants.PHYSIO_PRESCRIPTION_MAX_HEIGHT : AppConstants.PRESCRIPTION_MAX_HEIGHT
        if (prescriptionContainer.clientHeight <= maxHeight) {
            prescription = [prescription]
            if (props.renderHandler) {
                props.renderHandler();
            }
        } else {
            const prescriptionContainer = document.getElementsByClassName("prescription-card-content-container")[0];
            const publicNotesEls = document.getElementById("publicNotes-subsection")
            if (publicNotesEls && !_isEmpty(publicNotesEls.children)) {
                let updatedNotes: string[] = []
                _map(publicNotesEls.children, (childNode) => {
                    updatedNotes.push(childNode.innerHTML)
                })
                prescription.publicNotes = updatedNotes
            }
            prescription = AppUtils.formatPDFPages(
                prescription,
                prescriptionContainer,
                maxHeight,
                40
            );
        }
        setPrescriptionList(prescription);
        updatePdfData()
    }, []);

    // ComponentDidUpdate
    React.useEffect(() => {
        if (props.isTreatmentPlan
            || (
                !props.isTreatmentPlan
                && props.consultationData.signatureImagePreSignedUrl
                && props.consultationData.signatureImagePreSignedUrl.url
            )
        ) {
            updatePdfData();
        }
        if (!pdfGenerated && props.renderHandler) {
            props.renderHandler();
            setPdfGenerated(true)
        }
    }, [props.consultationData && props.consultationData.signatureImagePreSignedUrl, prescriptionList])

    const updatePdfData = () => {
        const updatedTemplate = prescriptionList.map(getModifyTemplate);
        setTemplate(updatedTemplate);
    }

    const getFormatPrescription = (): any => {
        return props.isPhysioConsultation
            ? physioPrescriptionFormatState(props.consultationData)
            : props.isTreatmentPlan
                ? treatmentPlanFormatState(props.treatmentPlanData)
                : doctorPrescriptionFormatState(props.consultationData);
    }

    const getModifyTemplate = (prescription: Readonly<any>, pageIndex?: number, templateList?: any[]): string => {
        return props.isPhysioConsultation
            ? physioPrescriptionModifyTemplate(props.pdfTemplates, props.consultationData, prescription, pageIndex, templateList)
            : props.isTreatmentPlan
                ? treatmentPlanModifyTemplate(props.pdfTemplates, props.treatmentPlanData, prescription, pageIndex, templateList)
                : doctorPrescriptionModifyTemplate(props.pdfTemplates, props.consultationData, prescription, pageIndex, templateList);
    }

    const constructor = () => {
        if (hasBeenCalled.current) return;
        const prescription: any = getFormatPrescription();
        setPrescriptionList([prescription]);
        setTemplate([getModifyTemplate(prescription)]);
        hasBeenCalled.current = true;
    }

    constructor();
    return (<div className={`prescription-template-container ${pdfGenerated ? "" : "visibility-hidden"}`} dangerouslySetInnerHTML={{ __html: template.join("\n") }} />);
}

export default CommonPrescriptionPdf